import { LitElement, classMap, css, html, nothing } from '@lit'

class NovaProductCardSwatches extends LitElement {
  static properties = {
    swatches: { type: Array },
    url: {
      type: String,
    },
    variants: {
      type: Array,
    },
    activeId: {
      type: String,
      attribute: 'active-id',
    },
    swatchesData: {
      type: Array,
    },
    isQuickShop: {
      type: Boolean,
      attribute: 'is-quick-shop',
    },
    variantUrls: {
      type: Object 
    },
  }

  constructor() {
    super()
    this.isQuickShop = false
    this.variantUrls = {}
  }

  sanitizeString(string) {
    return string
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
  }

  findVariantBySwatch(swatch) {
    return Array.from(this.variants).find((variant) => variant.options.includes(swatch))
  }

  findVariantUrlById(variantId) {
    if (!this.variantUrls) return this.url
    return this.variantUrls[variantId] || this.url
  }

  availableBySwatch(swatch) {
    return !(!Array.from(this.variants).find((variant) => variant.options.includes(swatch) && variant.available))
  }

  handleSwatchClick(event) {
    const { variantId, swatch, variantUrl } = event.target.dataset
    if (!variantId) return

    this.dispatchEvent(
      new CustomEvent('swatch-selected', {
        bubbles: true,
        composed: true,
        detail: { variantId: variantId, swatch: swatch, variantUrl: variantUrl },
      }),
    )
  }

  renderSwatch = (swatch, index) => {
    const swatchColor = this.swatchesData?.[index]?.color || ''
    const variant = this.findVariantBySwatch(swatch)
    if (!variant) return nothing

    const variantUrl = this.findVariantUrlById(variant?.id)
    const stateClasses = {
      'product-card__swatch--active': variant?.id === this.activeId,
      'is-quick-shop': this.isQuickShop,
      'product-card__swatch--sold-out': !this.availableBySwatch(swatch),
    }

    return html`
      <button
        class="product-card__swatch ${classMap(stateClasses)}"
        style="--swatch-background: ${swatchColor};"
        data-variant-id=${variant?.id}
        data-variant-url=${variantUrl}
        data-swatch=${swatch}
        aria-label=${swatch}
        @click=${this.handleSwatchClick}>
      </button>
    `
  }

  render() {

    return html`
      ${this.swatches ? html`
      <div class="product-card__swatch-container">
        ${this.swatches?.map(this.renderSwatch)}
        ${
          this.swatches?.length > 5
            ? html`<span class="product-card__swatch-container__trigger mobile"><a href=${this.url}>+${this.swatches.length - 5}</a></span>`
            : ''
        }
        ${
          this.swatches?.length > 10
            ? html`<span class="product-card__swatch-container__trigger desktop"><a href=${this.url}>+${this.swatches.length - 10}</a></span>`
            : ''
        }
      </div>
      ` : nothing}
    `
  }

  static styles = css`
    .product-card__swatch-container {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      width: fit-content;
    }

    .product-card__swatch-container--carousel {
      width: 100%;
      padding: 0;
    }

    .product-card__swatch {
      width: var(--nova-product-color-swatch-size);
      height: var(--nova-product-color-swatch-size);
      max-width: var(--nova-product-color-swatch-size);
      text-indent: -9999px;
      position: relative;
      padding: 0;
      background: var(--swatch-background);
      border: 1px solid var(--nova-color-grey-300);
      cursor: pointer;

      @media screen and (max-width: 989px) {
        &:nth-child(1n+6) {
          display: none;
        }
      }

      @media screen and (min-width: 990px) {
        &:nth-child(1n+12) {
          display: none;
        }
      }
    }

    .product-card__swatch + .product-card__swatch {
      margin-left: var(--nova-space-2xs);
    }

    .product-card__swatch--active {
      border: 1px solid var(--nova-brand-ink);
    }

    .product-card__swatch--active.is-quick-shop {
      border-color: var(--nova-color-white);
    }

    .product-card__swatch--active::after {
      content: "";
      position: absolute;
      top: 1px;
      left: 1px;
      right: 1px;
      bottom: 1px;
      background: var(--swatch-background);
    }

    .product-card__swatch--sold-out {
      border: 1px solid var(--nova-color-grey-300);
    }

    .product-card__swatch--sold-out::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, transparent calc(50% - 1px), var(--nova-color-grey-300) 50%, transparent calc(50% + 1px));
      pointer-events: none;
    }

    .product-card__swatch:nth-child(n+11) {
      display: none;
    }

    .product-card-1,
    .product-card-2 {
      & .product-card__swatch-container__trigger a {
        color: var(--nova-color-grey-400);
      }
    }

    .product-card__swatch-container__trigger {
      margin-left: 8px;
      width: max-content;

      @media screen and (max-width: 989px) {
        &.desktop {
          display: none;
        }
      }

      @media screen and (min-width: 990px) {
        &.mobile {
          display: none;
        }
      }
    }

    .product-card__swatch-container__trigger a {
      font: var(--nova-body-font);
      font-size: 14px;
      line-height: 1.35;
      color: var(--nova-color-grey-400);
      text-decoration: none;
    }

    // hack for shoelace and active state on first element
    sl-carousel::part(scroll-container) {
      padding-left: 1px;
    }

    sl-carousel-item {
      width: calc(var(--nova-product-color-swatch-size-active) + 1px);
      height: var(--nova-product-color-swatch-size);
    }

    sl-carousel-item button {
      width: var(--nova-product-color-swatch-size);
    }

    @media screen and (min-width: 768px) {
      .product-card__content {
        padding: 16px 16px 0;
      }
    }
  `
}

customElements.define('nova-product-card-swatches', NovaProductCardSwatches)
